import React from "react";
import styled from "@emotion/styled";
import { Transaction } from "../types";
import { useNavigate } from "react-router-dom";
import { setToBeRefunded } from "../store/checkoutSlice";
import { useDispatch } from "react-redux";
import { isNegative, isRefunded } from "../utils/transactionHelpers";
interface TransactionItemProps {
  transaction: Transaction;
  isUserTransaction?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px;
  padding: 8px 16px;
  margin-bottom: 12px;
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const Name = styled.span`
  font-family: "Rethink-Sans-SemiBold", "sans-serif";
  font-size: 14px;
  color: #000;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const TimeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #a7a7a7;
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const Time = styled.span`
  font-family: "Rethink-Sans-Medium", "sans-serif";
`;

const Dot = styled.span`
  font-family: "Rethink-Sans-Regular", "sans-serif";
  width: 5px;
  text-align: center;
`;

const StatusRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #a7a7a7;
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const Status = styled.span`
  font-family: "Rethink-Sans-Medium", "sans-serif";
`;

const CheckNumber = styled.span`
  font-family: "Rethink-Sans-Medium", "sans-serif";
`;

const AmountContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  height: 100%;
`;

const Amount = styled.span`
  font-family: "Rethink-Sans-SemiBold", "sans-serif";
  font-size: 14px;
  color: #000;
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const OriginalAmount = styled.span`
  font-family: "Rethink-Sans-Regular", "sans-serif";
  font-size: 12px;
  color: #a7a7a7;
  text-decoration: line-through;
  margin-bottom: 2px;
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const RefundButton = styled.button`
  background: #f3b900;
  color: #0f0f0f;
  font-family: "Rethink-Sans-Bold", "sans-serif";
  font-size: 8px;
  border: none;
  border-radius: 12px;
  padding: 0 16px;
  height: 24px;
  margin-top: 4px;
  cursor: pointer;
  @media (min-width: 768px) {
    font-size: 14px;
    padding: 0 24px;
    height: 32px;
    border-radius: 16px;
  }
`;

const Divider = styled.div`
  background: #efefef;
  height: 1px;
  width: 100%;
`;

function formatTimeAndDate(created_at: string) {
  const dateObj = new Date(created_at);
  const time = dateObj.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const date = dateObj.toLocaleDateString([], {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  return { time, date };
}

const TransactionItem: React.FC<TransactionItemProps> = ({ transaction }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    user_name,
    created_at,
    check_number,
    amount,
    original_amount,
    refundable,
    refund_status,
    credit_type,
  } = transaction;
  const { time, date } = formatTimeAndDate(created_at);
  const handleRefund = () => {
    dispatch(setToBeRefunded(transaction));
    navigate("/refund");
  };
  return (
    <div>
      <Container>
        <Info>
          <UserInfo>
            <NameRow>
              <Name>{user_name}</Name>
            </NameRow>
            <TimeRow>
              <Time>{time}</Time>
              <Dot>•</Dot>
              <Time>{date}</Time>
            </TimeRow>
            <StatusRow>
              <Status>{credit_type}</Status>
              {check_number && (
                <>
                  <Dot>•</Dot>
                  <CheckNumber>Check #{check_number}</CheckNumber>
                </>
              )}
              {isRefunded(refund_status) && (
                <>
                  <Dot>•</Dot>
                  <Status>{refund_status}</Status>
                </>
              )}
            </StatusRow>
          </UserInfo>
        </Info>
        <AmountContainer>
          {isRefunded(refund_status) && (
            <OriginalAmount>
              ${Number(original_amount).toFixed(2)}
            </OriginalAmount>
          )}
          <Amount>
            {isNegative(transaction) ? "-" : ""}${Number(amount).toFixed(2)}
          </Amount>
          {refundable && (
            <RefundButton onClick={handleRefund}>Refund</RefundButton>
          )}
        </AmountContainer>
      </Container>
      <Divider />
    </div>
  );
};

export default TransactionItem;
